/**
 * Esquemas de validación para Catálogos
 * Usando Yup para validación de formularios
 */
import * as yup from 'yup';
import { isValidEmail, isValidRUC, isValidPassword } from '../utils/validators';

// ============================================
// BANCO
// ============================================

export const bancoSchema = yup.object().shape({
  nombre: yup
    .string()
    .required('El nombre es requerido')
    .min(3, 'El nombre debe tener al menos 3 caracteres')
    .max(100, 'El nombre no puede exceder 100 caracteres'),

  codigo: yup
    .string()
    .required('El código es requerido')
    .matches(/^[0-9]+$/, 'El código debe contener solo números')
    .min(3, 'El código debe tener al menos 3 caracteres')
    .max(10, 'El código no puede exceder 10 caracteres'),

  activo: yup.boolean(),
});

// ============================================
// CLIENTE
// ============================================

export const clienteSchema = yup.object().shape({
  codigo: yup
    .string()
    .required('El código es requerido')
    .max(20, 'El código no puede exceder 20 caracteres'),

  razonSocial: yup
    .string()
    .required('La razón social es requerida')
    .min(3, 'La razón social debe tener al menos 3 caracteres')
    .max(200, 'La razón social no puede exceder 200 caracteres'),

  cuit: yup
    .string()
    .required('El CUIT/RUC es requerido')
    .test('valid-ruc', 'CUIT/RUC inválido', (value) => {
      if (!value) return false;
      return isValidRUC(value);
    }),

  direccion: yup.string().max(250, 'La dirección no puede exceder 250 caracteres'),

  telefono: yup.string().max(50, 'El teléfono no puede exceder 50 caracteres'),

  email: yup
    .string()
    .email('Email inválido')
    .test('valid-email', 'Email inválido', (value) => {
      if (!value) return true; // Email es opcional
      return isValidEmail(value);
    }),

  activo: yup.boolean(),
});

// ============================================
// CUENTA BANCARIA
// ============================================

export const cuentaBancariaSchema = yup.object().shape({
  empresa_id: yup.string().required('Debe seleccionar una empresa'),

  banco_id: yup.string().required('Debe seleccionar un banco'),

  numero_cuenta: yup
    .string()
    .required('El número de cuenta es requerido')
    .matches(/^[0-9-]+$/, 'El número de cuenta debe contener solo números y guiones')
    .min(8, 'El número de cuenta debe tener al menos 8 caracteres')
    .max(30, 'El número de cuenta no puede exceder 30 caracteres'),

  tipo_cuenta: yup
    .string()
    .required('Debe seleccionar el tipo de cuenta')
    .oneOf(['AHORROS', 'CORRIENTE', 'CHEQUES'], 'Tipo de cuenta inválido'),

  saldo_actual: yup
    .number()
    .required('El saldo actual es requerido')
    .min(0, 'El saldo no puede ser negativo'),

  moneda: yup
    .string()
    .required('Debe seleccionar una moneda')
    .oneOf(['USD', 'VES', 'EUR', 'ARS'], 'Moneda inválida'),

  titular: yup.string().max(200, 'El titular no puede exceder 200 caracteres'),

  activo: yup.boolean(),
});

// ============================================
// USUARIO
// ============================================

export const usuarioSchema = yup.object().shape({
  empresa_id: yup
    .number()
    .required('Debe seleccionar una empresa')
    .positive('Debe seleccionar una empresa válida'),

  nombre: yup
    .string()
    .required('El nombre es requerido')
    .min(3, 'El nombre debe tener al menos 3 caracteres')
    .max(100, 'El nombre no puede exceder 100 caracteres'),

  apellido: yup
    .string()
    .required('El apellido es requerido')
    .min(3, 'El apellido debe tener al menos 3 caracteres')
    .max(100, 'El apellido no puede exceder 100 caracteres'),

  email: yup
    .string()
    .required('El email es requerido')
    .email('Email inválido')
    .test('valid-email', 'Email inválido', (value) => {
      if (!value) return false;
      return isValidEmail(value);
    }),

  password: yup
    .string()
    .required('La contraseña es requerida')
    .min(6, 'La contraseña debe tener al menos 6 caracteres')
    .test('valid-password', 'Contraseña muy débil', (value) => {
      if (!value) return false;
      return isValidPassword(value);
    }),

  confirmPassword: yup
    .string()
    .required('Debe confirmar la contraseña')
    .oneOf([yup.ref('password')], 'Las contraseñas no coinciden'),

  rol: yup
    .string()
    .required('Debe seleccionar un rol')
    .oneOf(['administrador', 'cajero', 'contador', 'auditor'], 'Rol inválido'),

  activo: yup.boolean(),
});

/**
 * Esquema para editar usuario (sin password)
 */
export const usuarioEditSchema = yup.object().shape({
  empresa_id: yup
    .number()
    .required('Debe seleccionar una empresa')
    .positive('Debe seleccionar una empresa válida'),

  nombre: yup
    .string()
    .required('El nombre es requerido')
    .min(3, 'El nombre debe tener al menos 3 caracteres')
    .max(100, 'El nombre no puede exceder 100 caracteres'),

  apellido: yup
    .string()
    .required('El apellido es requerido')
    .min(3, 'El apellido debe tener al menos 3 caracteres')
    .max(100, 'El apellido no puede exceder 100 caracteres'),

  email: yup
    .string()
    .required('El email es requerido')
    .email('Email inválido')
    .test('valid-email', 'Email inválido', (value) => {
      if (!value) return false;
      return isValidEmail(value);
    }),

  rol: yup
    .string()
    .required('Debe seleccionar un rol')
    .oneOf(['administrador', 'cajero', 'contador', 'auditor'], 'Rol inválido'),

  activo: yup.boolean(),
});

/**
 * Esquema para cambiar contraseña
 */
export const cambiarPasswordSchema = yup.object().shape({
  passwordActual: yup.string().required('La contraseña actual es requerida'),

  passwordNueva: yup
    .string()
    .required('La nueva contraseña es requerida')
    .min(6, 'La contraseña debe tener al menos 6 caracteres')
    .notOneOf([yup.ref('passwordActual')], 'La nueva contraseña debe ser diferente a la actual'),

  confirmPassword: yup
    .string()
    .required('Debe confirmar la contraseña')
    .oneOf([yup.ref('passwordNueva')], 'Las contraseñas no coinciden'),
});

/**
 * Esquema para que el administrador restablezca la contraseña de un usuario
 * (no requiere la contraseña actual del usuario afectado)
 */
export const resetPasswordSchema = yup.object().shape({
  password: yup
    .string()
    .required('La nueva contraseña es requerida')
    .min(6, 'La contraseña debe tener al menos 6 caracteres')
    .test('valid-password', 'Contraseña muy débil', (value) => {
      if (!value) return false;
      return isValidPassword(value);
    }),

  confirmPassword: yup
    .string()
    .required('Debe confirmar la contraseña')
    .oneOf([yup.ref('password')], 'Las contraseñas no coinciden'),
});

export default {
  bancoSchema,
  clienteSchema,
  cuentaBancariaSchema,
  usuarioSchema,
  usuarioEditSchema,
  cambiarPasswordSchema,
  resetPasswordSchema,
};
